import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Tabs, Tab, Box, Button, LinearProgress,
  TextField, FormControl, InputLabel, Select, MenuItem,
  Typography
} from "@mui/material";

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

export default function EditPromptDialog({
  open,
  initialPrompt      = "",
  initialSize        = { w: "100%", h: "400" },
  busy               = false,
  onRegenerate,      // (prompt, accuracyLevel)        → void
  onResize,          // ({ w, h })                     → void
  onDelete,          // ()                             → void
  onCancel,
}) {
  /* ───────────────────────── state ───────────────────────── */
  const [tab, setTab]                 = useState(0);
  const [prompt, setPrompt]           = useState(initialPrompt);
  const [accuracy, setAccuracy]       = useState("standard");
  const [size, setSize]               = useState(initialSize);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  useEffect(() => {
    // reset every time the dialog re-opens
    if (open) {
      setTab(0);
      setPrompt(initialPrompt);
      setAccuracy("standard");
      setSize(initialSize);
      setDeleteConfirm(false);
    }
  }, [open]);

  /* ───────────────────────── render ───────────────────────── */
  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="sm" PaperProps={{ sx: { p: 2 } }}>
      <DialogTitle>Edit visual</DialogTitle>
      {busy && <LinearProgress />}

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="fullWidth"
        sx={{ borderBottom: 1, borderColor: "divider" }}
      >
        <Tab label="Prompt" />
        <Tab label="Resize" />
        <Tab label="Delete" />
      </Tabs>

      {/* ────────── Prompt tab ────────── */}
      <TabPanel value={tab} index={0}>
        <TextField
          fullWidth
          multiline
          minRows={4}
          label="LLM prompt"
          value={prompt}
          onChange={e => setPrompt(e.target.value)}
        />
        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel id="acc-label">Accuracy level</InputLabel>
          <Select
            labelId="acc-label"
            value={accuracy}
            label="Accuracy level"
            onChange={e => setAccuracy(e.target.value)}
          >
            <MenuItem value="standard">Standard LLM</MenuItem>
            <MenuItem value="advanced">Advanced LLM</MenuItem>
          </Select>
        </FormControl>
      </TabPanel>

      {/* ────────── Resize tab ────────── */}
      <TabPanel value={tab} index={1}>
        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            label="Width"
            fullWidth
            placeholder="e.g. 100%, 600px, 50vw"
            helperText="Any valid CSS length(px, %, vw, rem)"
            value={size.w}
            onChange={e => setSize({ ...size, w: e.target.value })}
          />
          <TextField
            label="Height"
            fullWidth
            placeholder="e.g. 400, 400px, 50vh"
            helperText="Any valid CSS length(px, %, vh, rem)"
            value={size.h}
            onChange={e => setSize({ ...size, h: e.target.value })}
          />
        </Box>
        <Button
          sx={{ mt: 2 }}
          variant="contained"
          onClick={() => onResize(size)}
        >
          Apply
        </Button>
      </TabPanel>

      {/* ────────── Delete tab ────────── */}
      <TabPanel value={tab} index={2}>
        {!deleteConfirm ? (
          <>
            <Typography>
              This will permanently remove the visual from the lesson. You can’t
              undo this action.
            </Typography>
            <Button
              sx={{ mt: 2 }}
              color="error"
              variant="contained"
              onClick={() => setDeleteConfirm(true)}
            >
              Delete visual
            </Button>
          </>
        ) : (
          <>
            <Typography>Are you absolutely sure?</Typography>
            <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                onClick={() => setDeleteConfirm(false)}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={onDelete}
              >
                Yes, delete
              </Button>
            </Box>
          </>
        )}
      </TabPanel>

      {/* ────────── bottom buttons (Prompt tab only) ────────── */}
      {tab === 0 && (
        <DialogActions>
          <Button disabled={busy} onClick={onCancel}>Close</Button>
          <Button
            disabled={busy || !prompt.trim()}
            variant="contained"
            onClick={() => onRegenerate(prompt, accuracy)}
          >
            {busy ? "Regenerating…" : "Regenerate"}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
